import { capitalize } from '@mui/material'
import { PartialMessage } from '@protobuf-ts/runtime'
import { RpcOptions, ServiceInfo, UnaryCall } from '@protobuf-ts/runtime-rpc'
import { AnyAction } from '@reduxjs/toolkit'
import useGetDefaultPageSize from 'hooks/useGetDefaultPageSize'
import { useOAuth } from 'lib/rpc'
import { useClientRPC } from 'lib/rpcts'
import { AllowedRefs, convertToRefString, WebsocketNotifyClient } from 'lib/websocket-notify'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { ThunkDispatch } from 'redux-thunk'
import { ListFlags } from 'redux_toolkit/lib/GRPCSubscriptionPagedList'
import { action } from './actions'
import { InvalidateHook, InvalidateHookName } from './createUnaryApi'
import { Flags } from './GRPCSubscriptionObject'
import { grpcReducerNamespace, ReduxListCache, ReduxListState, ReduxObjectState } from './reducer'
import {
    clientTypeName,
    GRPCClient,
    InferListRequest,
    InferListUnaryResponse,
    InferRequest,
    InferUnaryResponse,
    InferUnaryResponseListType,
    ListableRequest,
    serializeQuery,
    UnaryMethod,
} from './utils'

export const rpcNotReadyErr = new Error('List call is not actionable')

export type ListHookFlags = Omit<Flags<unknown>, 'transformResponse' | 'notify'> & {
    notify?: boolean
    pageSize?: number
    pages?: number
    actionable?: boolean
    // This is a temporary fix for a very specific campaign builder use case.
    disableEssentialTableExistenceCheck?: boolean
    autoReload?: boolean
}

type PollOnRefsFnType = (refs: Array<AllowedRefs>, interval?: number) => void
type getPageFnType = (pageNumber?: number, force?: boolean) => Promise<void>

export type ListHookName<S extends string> = `use${Capitalize<S>}`
export type ListResponse<ResponseType> = ReduxListCache<Readonly<ResponseType>> & {
    getPage: getPageFnType
    pollOnRefs: PollOnRefsFnType
}

export type HookLike<Req, ResponseType> = (
    args: Req,
    flags?: ListHookFlags
) => ReduxListCache<ReadonlyArray<ResponseType>> & {
    getPage: getPageFnType
    pollOnRefs: PollOnRefsFnType
}

type UseListHook<Client, Method extends keyof Client & string, ResponseType = InferUnaryResponse<Client[Method]>> = (
    args: InferRequest<Client[Method]>,
    flags?: ListHookFlags
) => ListResponse<ResponseType>

type UseLazyListHook<
    Client,
    Method extends keyof Client & string,
    ResponseType = InferUnaryResponse<Client[Method]>
> = () => {
    trigger: (args: InferRequest<Client[Method]>, opts?: ListHookFlags) => { getPage?: getPageFnType, request?: Promise<unknown> }
} & ReduxListCache<ResponseType>
type InvalidateTagHookName<S extends string> = `useInvalidate${Capitalize<S>}Tag`
type InvalidateTagHook = () => (item: PartialMessage<object>) => void
type ListLazyHookName<S extends string> = `useLazy${Capitalize<S>}`

type ListHooks<Client extends ServiceInfo, Method extends keyof Client & string, ResponseType> = Record<
    ListHookName<Method>,
    UseListHook<Client, Method, ResponseType>
> &
    Record<InvalidateHookName<Method>, InvalidateHook<Client, Method>> &
    Record<InvalidateTagHookName<Method>, InvalidateTagHook> &
    Record<ListLazyHookName<Method>, UseLazyListHook<Client, Method, ResponseType>>

type CreateApiForListReturn<
    Client extends ServiceInfo,
    Method extends keyof Client & string,
    Res extends object
> = ListHooks<Client, Method, Res> & { reducer: string }

export type InvokePagedListRPC<Req extends object, Res extends object> = (
    pageToken: string,
    pageSize: number,
    opts: RpcOptions,
    ref?: Array<AllowedRefs>
) => UnaryCall<Req, Res>

export const DEFAULT_LIST_STATE = Object.freeze({
    isLoading: true,
    isBackgroundLoading: false,
    isError: false,
    error: undefined,
    data: [],
    canLoadMore: true,
    shouldInvalidateList: false,
    metadata: {},
    lastPollTimeForRef: {},
})

export const DEFAULT_LAZY_LIST_STATE = Object.freeze({
    ...DEFAULT_LIST_STATE,
    isLoading: false,
})

/**
 * Creates a set of hooks and a reducer entry to manage paginated list data
 * for a specific gRPC service method (ie ListApps, ListUsers). The generated
 * hooks provide subscription, invalidation, and lazy-loading capabilities.
 *
 * @param {GRPCClient<Client>} ClientConstructor The protobuf-generated constructor for your generated gRPC client.
 * @param {Method} methodName The gRPC method name that fetches a list.
 * @param {(i: Res) => TransformedRes} [transformResponse] An optional transformation function
 *        that can be applied to the raw response.
 * @param {string} [tag] The typeName of a ref object, used to match websocket updates to this list.
 * @returns {CreateApiForListReturn<Client, Method, TransformedRes>} An object containing:
 *   - reducer: the namespace used in the Redux store,
 *   - use{methodName} hooks that interact with the Redux store and the underlying gRPC method,
 *   - a lazy variant of the hook
 *   - invalidation hooks for both the entire list and for individual tags.
 */
export const createPagedListApi = <
    Client extends ServiceInfo,
    Method extends keyof Client & string,
    Req extends InferListRequest<Client[Method]>,
    Res extends InferListUnaryResponse<Client[Method]>,
    TransformedRes extends object = InferUnaryResponseListType<Client[Method]>
>(
        ClientConstructor: GRPCClient<Client>,
        methodName: Client[Method] extends (input: any, options?: RpcOptions) => UnaryCall<Req, Res> ? Method : never, // eslint-disable-line @typescript-eslint/no-explicit-any
        transformResponse?: (i: Res) => TransformedRes,
        tag?: string,
    ): CreateApiForListReturn<Client, Method, TransformedRes> => {
    type Request = InferListRequest<Client[Method]>
    type Selector = (state: ReduxListState<TransformedRes>) => ReduxListCache<TransformedRes>

    const namespace = clientTypeName(ClientConstructor)

    const subscribeList = (
        key: string,
        client: Client,
        request: Request,
        flags: ListFlags<TransformedRes> = {},
    ): ((
        dispatch: ThunkDispatch<ReduxListState<object>, never, AnyAction>,
        getState: () => ReduxListState<object>
    ) => Promise<unknown>) =>
        action.subscribeList<Req, Res>(
            namespace,
            key,
            (pageToken, pageSize, opts, refs) => {
                const listableReq = JSON.parse(JSON.stringify(request)) as ListableRequest
                listableReq.pageSize = pageSize
                if (pageToken) {
                    listableReq.pageToken = pageToken
                } else {
                    listableReq.pageToken = ''
                }
                if (refs && refs.length > 0) {
                    listableReq.refs = refs
                }

                const m = client[methodName] as UnaryMethod<Request>
                return m.call(client, listableReq as Request, opts)
            },
            { ...flags, transformResponse, tag },
        )

    const useHook: UseListHook<Client, Method, TransformedRes> = (args: Request, flags?: ListHookFlags) => {
        // TODO(kans): warn dev if flags aren't stable, because we won't update flags on active subs atm
        const client = useClientRPC(ClientConstructor)
        const authClient = useOAuth()
        const notify = useMemo(() => {
            return flags?.notify
                ? WebsocketNotifyClient.getInstance(authClient)
                : undefined
        }, [authClient, flags?.notify])

        const defaultPageSize = useGetDefaultPageSize()
        const pageSize = flags?.pageSize ?? defaultPageSize

        const { key, selector } = useMemo(() => {
            const k = serializeQuery(methodName, args)
            const s: Selector = (state) => state[grpcReducerNamespace]?.[namespace]?.[key] ?? DEFAULT_LIST_STATE
            return { key: k, selector: s }
        }, [args])

        const dispatch: ThunkDispatch<ReduxListState<TransformedRes>, never, AnyAction> = useDispatch()
        useEffect(() => {
            if (!client || flags?.actionable === false) {
                return
            }
            dispatch(subscribeList(key, client, args, { ...flags, notify, pageSize }))
            return () => void dispatch(action.unsubscribe(namespace, key))
        }, [key, client, flags?.actionable]) // eslint-disable-line react-hooks/exhaustive-deps

        const selection = useSelector(selector)
        const getPage = useCallback(
            async (pageNumber = 1, force = false) => {
                await dispatch(action.getPage(namespace, key, pageNumber, force))
            },
            [dispatch, key],
        )

        const pollOnRefs = useCallback(
            (refs: Array<AllowedRefs>, interval = 5000, cancelExisting = false) => {
                const cacheKey = serializeQuery(methodName, args || null)
                dispatch(action.poll(namespace, cacheKey, refs, interval, cancelExisting))
            },
            [args, dispatch],
        )

        return useMemo(() => ({ ...selection, getPage, pollOnRefs }), [selection, getPage, pollOnRefs])
    }

    /*
     * Invalidates everything without args
     * */
    const useInvalidate: InvalidateHook<Client, Method> = (args?: Request) => {
        const dispatch: ThunkDispatch<ReduxObjectState<TransformedRes>, never, AnyAction> = useDispatch()
        const key = serializeQuery(methodName, args || null)
        const invalidateAll = !args
        return useCallback(
            () => dispatch(action.invalidateList(namespace, key, invalidateAll)),
            [dispatch, key, invalidateAll],
        )
    }

    const useInvalidateWithTag: InvalidateTagHook = () => {
        const dispatch: ThunkDispatch<ReduxObjectState<TransformedRes>, never, AnyAction> = useDispatch()

        return useCallback(
            (item: PartialMessage<object>) => {
                const refString = convertToRefString(item, tag)
                if (!refString) {
                    throw new Error('cannot convert to ref')
                }
                dispatch(action.invalidateTag(tag || '', refString, item))
            },
            [dispatch],
        )
    }

    const useLazyHook: UseLazyListHook<Client, Method, TransformedRes> = () => {
        // TODO(kans): warn dev if flags aren't stable, because we won't update flags on active subs atm
        const client = useClientRPC(ClientConstructor)
        const authClient = useOAuth()
        const dispatch: ThunkDispatch<ReduxListState<TransformedRes>, never, AnyAction> = useDispatch()
        const ref = useRef<{ key: string; args: Request; unsub:() => void; getPage: () => Promise<void> }>()
        const [selector, setSelector] = useState<Selector>(() => () => DEFAULT_LAZY_LIST_STATE)
        const defaultPageSize = useGetDefaultPageSize()

        // creates a new trigger when we get a new client :-/
        const trigger = useMemo(() => (args: Request, flags?: ListHookFlags) => {
            if (!client) {
                return { }
            }

            const key = serializeQuery(methodName, args)
            const notify =
                flags?.notify
                    ? WebsocketNotifyClient.getInstance(authClient)
                    : undefined

            // This can refetch stale data...
            // we could look to see if it's stale before doing this!
            ref.current?.unsub?.()
            if (flags?.actionable === false) {
                return { }
            }

            const pageSize = flags?.pageSize ?? defaultPageSize
            const request = dispatch(subscribeList(key, client, args, { ...flags, notify, pageSize }))

            const getPage = async (pageNumber = 1, force = false): Promise<void> => {
                await dispatch(action.getPage(namespace, key, pageNumber, force))
            }

            if (ref.current?.key === key) {
                return { getPage: ref.current.getPage, request }
            }

            ref.current = {
                key,
                args,
                getPage,
                unsub: () => void dispatch(action.unsubscribe(namespace, key)),
            }

            const newSelector = (state: ReduxListState<TransformedRes>): ReduxListCache<TransformedRes> => {
                if (!key) {
                    return DEFAULT_LAZY_LIST_STATE
                }
                return state[grpcReducerNamespace]?.[namespace]?.[key] || DEFAULT_LAZY_LIST_STATE
            }
            setSelector(() => newSelector)

            return { getPage: ref.current.getPage, request }
        }, [client, authClient, dispatch, defaultPageSize])

        // State doesn't seem to live this long, so we use a ref :-/
        useEffect(() => () => ref.current?.unsub?.(), [])

        // useSelector swaps out the subscription every time the selector changes
        const selection = useSelector(selector)
        return useMemo(() => ({ ...selection, trigger }), [selection, trigger])
    }

    const capitalized = capitalize(methodName)
    return {
        reducer: namespace,
        [`use${capitalized}`]: useHook,
        [`useInvalidate${capitalized}`]: useInvalidate,
        [`useInvalidate${capitalized}Tag`]: useInvalidateWithTag,
        [`useLazy${capitalized}`]: useLazyHook,
    } as CreateApiForListReturn<Client, Method, TransformedRes>
}
